import { tallyTokens } from "../../theme.tsx";
import React from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import TopBar from "../conversation/TopBar";
import SETTINGS from "../../lib/TallyLibrary/constants/configGeneral.json";

interface TilePageProps {
  title: React.ReactNode;
  subtitle: React.ReactNode;
  openDrawer: boolean;
  setOpenDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  children: React.ReactNode;
  /** Optional element rendered inside the fixed panel but outside the scroll area (e.g. a FAB). */
  action?: React.ReactNode;
}

const TilePage: React.FC<TilePageProps> = ({
  title,
  subtitle,
  openDrawer,
  setOpenDrawer,
  children,
  action,
}) => {
  const theme = useTheme();
  const isScreenBiggerThanOrEqualToMd = useMediaQuery(
    theme.breakpoints.up("md"),
  );

  return (
    <Box>
      <Box
        sx={[
          {
            // Matches ConversationPanel: the page fills the viewport and the
            // content inside is what carries a max width.
            position: "fixed",
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: tallyTokens.canvas,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            transition: theme.transitions.create(["left"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          },
          isScreenBiggerThanOrEqualToMd &&
            openDrawer && {
              left: `${SETTINGS.drawerWidth}px`,
              transition: theme.transitions.create(["left"], {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
        ]}
      >
        <TopBar
          title={title}
          onOpenDrawer={openDrawer ? undefined : () => setOpenDrawer(true)}
        />
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            position: "relative",
            zIndex: 1,
            px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 },
            // The top bar is in the flow above; this is the design's inset.
            pt: "26px",
            pb: { xs: 3, sm: 4 },
            scrollbarColor:
              "var(--tally-scroll-thumb) var(--tally-scroll-track)",
          }}
        >
          {/* Cap the content width so ultra-wide monitors stay sane; below
              that, large screens get more cards per row. */}
          <Box sx={{ width: "100%", maxWidth: "2200px", mx: "auto" }}>
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h5"
                fontWeight="bold"
                color="text.primary"
                sx={{ letterSpacing: "-0.02em" }}
              >
                {title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {subtitle}
              </Typography>
            </Box>
            {/* As many columns as fit at 400px or more: two on a laptop,
                more on large screens, one on phones. */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fill, minmax(min(100%, 400px), 1fr))",
                gap: 2,
              }}
            >
              {children}
            </Box>
          </Box>
        </Box>
        {action}
      </Box>
    </Box>
  );
};

export default TilePage;
